import type { DocumentTransformation, TargetSchema, TransformationTemplate, ExtractedRecord } from "@/types";
import { SchemaDefinitionsResponse } from "@/types/schemaDefinitions.ts";
import { getRecordData, getRecordSchemas } from "./recordUtils";

export interface MappingValidationError {
  type: "duplicate_target" | "unknown_field" | "mutually_exclusive";
  targetField: string;
  sourceHeaders: string[];
  message: string;
}

/**
 * Composite/parts field pairs where mapping both the composite and any split
 * part simultaneously is invalid. Fires only when the composite AND at least
 * one part are both present — mapping the split pair alone is fine.
 */
const MUTUALLY_EXCLUSIVE_GROUPS: { composite: string; parts: string[] }[] = [
  {
    composite: "claimant_related_subscriber_name",
    parts: ["claimant_related_subscriber_first_name", "claimant_related_subscriber_last_name"],
  },
  {
    composite: "claimant_name",
    parts: ["claimant_first_name", "claimant_last_name"],
  },
];

export interface ValidationIssue {
  schema: TargetSchema;
  message: string;
}

/**
 * Validate mappings for duplicate target fields
 * @param mappings - Record mapping source headers to target fields
 * @returns Array of validation errors for duplicates
 */
export const validateDuplicateMappings = (mappings: Record<string, string[]>): MappingValidationError[] => {
  const errors: MappingValidationError[] = [];
  const targetFieldMap: Record<string, string[]> = {};

  // Group source headers by their target fields
  Object.entries(mappings).forEach(([sourceHeader, targetFields]) => {
    if (!targetFields || targetFields.length === 0) return; // Skip unmapped

    targetFields.forEach((targetField) => {
      if (!targetFieldMap[targetField]) {
        targetFieldMap[targetField] = [];
      }
      targetFieldMap[targetField].push(sourceHeader);
    });
  });

  // Find duplicates
  Object.entries(targetFieldMap).forEach(([targetField, sourceHeaders]) => {
    if (sourceHeaders.length > 1) {
      errors.push({
        type: "duplicate_target",
        targetField,
        sourceHeaders,
        message: `Multiple headers mapped to "${targetField}": ${sourceHeaders.join(", ")}`,
      });
    }
  });

  return errors;
};

/**
 * Validate mappings for unknown/invalid fields
 * @param mappings - Record mapping source headers to target fields
 * @param availableFields - Set of valid field names for this schema
 * @returns Array of validation errors for unknown fields
 */
export const validateUnknownFields = (
  mappings: Record<string, string[]>,
  availableFields: Set<string>
): MappingValidationError[] => {
  const errors: MappingValidationError[] = [];

  Object.entries(mappings).forEach(([header, targetFields]) => {
    if (!targetFields || targetFields.length === 0) return; // Skip unmapped

    targetFields.forEach((targetField) => {
      if (!availableFields.has(targetField)) {
        errors.push({
          type: "unknown_field",
          targetField,
          sourceHeaders: [header],
          message: `"${targetField}" is not a valid field for this schema`,
        });
      }
    });
  });

  return errors;
};

/**
 * Validate mappings for mutually exclusive composite/parts conflicts.
 *
 * Fires only when the composite field AND at least one of its parts are both
 * mapped simultaneously. Mapping the split parts alone (without the composite)
 * is valid and does not produce errors.
 *
 * @param mappings - Record mapping source headers to target fields
 * @returns Array of validation errors for mutually exclusive conflicts
 */
export const validateMutuallyExclusiveFields = (mappings: Record<string, string[]>): MappingValidationError[] => {
  // Collect all active target fields (flat, across all headers)
  const allTargets = new Map<string, string[]>(); // target field → source headers that map to it
  Object.entries(mappings).forEach(([sourceHeader, targetFields]) => {
    if (!targetFields || targetFields.length === 0) return;
    targetFields.forEach((targetField) => {
      if (!allTargets.has(targetField)) {
        allTargets.set(targetField, []);
      }
      allTargets.get(targetField)!.push(sourceHeader);
    });
  });

  const errors: MappingValidationError[] = [];

  for (const group of MUTUALLY_EXCLUSIVE_GROUPS) {
    const compositePresent = allTargets.has(group.composite);
    const presentParts = group.parts.filter((part) => allTargets.has(part));

    // Only fire when composite AND at least one part are both mapped
    if (!compositePresent || presentParts.length === 0) continue;

    for (const field of [group.composite, ...presentParts]) {
      errors.push({
        type: "mutually_exclusive",
        targetField: field,
        sourceHeaders: allTargets.get(field)!,
        message: `Cannot map "${group.composite}" and its split fields simultaneously. Use one or the other.`,
      });
    }
  }

  return errors;
};

/**
 * Validate all edited schemas for mapping conflicts and unknown fields
 * @param editedMappingsBySchema - Mappings organized by schema key
 * @param editedSchemas - Set of schema keys that have been edited
 * @param availableFieldsBySchema - Map of schema keys to their valid field sets
 * @returns Record mapping schema keys to their validation errors (only includes schemas with errors)
 */
export const validateAllMappings = (
  editedMappingsBySchema: Record<string, Record<string, string[]>>,
  editedSchemas: Set<string>,
  availableFieldsBySchema: Record<string, Set<string>>
): Record<string, MappingValidationError[]> => {
  const errorsBySchema: Record<string, MappingValidationError[]> = {};

  editedSchemas.forEach((schemaKey) => {
    const mappings = editedMappingsBySchema[schemaKey];
    if (!mappings) return;

    const duplicateErrors = validateDuplicateMappings(mappings);
    const unknownErrors = validateUnknownFields(mappings, availableFieldsBySchema[schemaKey] || new Set());
    const mutuallyExclusiveErrors = validateMutuallyExclusiveFields(mappings);

    const allErrors = [...duplicateErrors, ...unknownErrors, ...mutuallyExclusiveErrors];
    if (allErrors.length > 0) {
      errorsBySchema[schemaKey] = allErrors;
    }
  });

  return errorsBySchema;
};

/**
 * Build current mappings from extracted record and template
 * Used for validating existing transformations
 */
const buildCurrentMappings = (
  extractedRecord: ExtractedRecord,
  template: TransformationTemplate,
  schema: TargetSchema
): Record<string, string[]> => {
  // Build header lookup from template
  const headerLookup: Record<string, string> = {};
  template.field_mappings
    ?.filter((m) => m.target_schema === schema && m.extraction_location?.key)
    .forEach((m) => {
      headerLookup[m.target_field] = m.extraction_location.key!;
    });

  // Build current mappings (source header -> target fields[])
  const currentMappings: Record<string, string[]> = {};
  const recordData = getRecordData(extractedRecord, schema);
  Object.keys(recordData).forEach((targetField) => {
    const sourceHeader = headerLookup[targetField];
    if (sourceHeader) {
      if (!currentMappings[sourceHeader]) {
        currentMappings[sourceHeader] = [];
      }
      currentMappings[sourceHeader].push(targetField);
    }
  });

  return currentMappings;
};

/**
 * Validate a single schema's template mapping
 * Used for validating existing transformations (not during editing)
 *
 * Reads the LOADED records, not `transformation.extracted_records` — the
 * transformation row stopped carrying records when the SPA moved to the
 * windowed endpoint (#6755), so that field is permanently undefined and a
 * validator reading it silently reports "no issues" for every schema,
 * disarming the approval gate on documents of every size.
 *
 * One record per schema is enough: the checks compare the record's data KEYS
 * against the template, and every record of a schema carries the same mapped
 * keys.
 *
 * @param records - Loaded extracted records (one per schema is sufficient)
 * @param template - The transformation template
 * @param schema - The target schema to validate
 * @param schemaDefinitions - The dynamic schema definitions
 * @param recordsComplete - True when every record is in hand (no more pages).
 *   On a PAGED document a schema's only records can live on an unloaded page;
 *   "couldn't validate" must then be reported, not silently passed — that
 *   would be the same disarm in a narrower form. When the set is complete, a
 *   schema with no record has vacuously nothing to validate.
 * @returns Array of validation issues for this schema
 */
export const validateTemplateMapping = (
  records: ExtractedRecord[],
  template: TransformationTemplate,
  schema: TargetSchema,
  schemaDefinitions: SchemaDefinitionsResponse | null | undefined,
  recordsComplete: boolean
): ValidationIssue[] => {
  const issues: ValidationIssue[] = [];

  const extractedRecord = records.find((r) => getRecordSchemas(r).includes(schema));

  if (!extractedRecord) {
    if (!recordsComplete) {
      issues.push({
        schema,
        message: "Mapping not validated: no record for this schema is loaded yet. Load more records to validate it.",
      });
    }
    return issues;
  }

  // Build current mappings
  const currentMappings = buildCurrentMappings(extractedRecord, template, schema);

  // Get available fields for this schema
  const availableFields = schemaDefinitions?.schemas[schema]?.fields || [];
  const validFieldNames = new Set(availableFields.map((f) => f.field));

  // If definitions aren't loaded, can't validate unknown fields. Skipping this prevents false positives during loading.
  if (validFieldNames.size > 0) {
    // Check for unknown fields
    const unknownFieldErrors = validateUnknownFields(currentMappings, validFieldNames);

    if (unknownFieldErrors.length > 0) {
      const unknownFieldNames = unknownFieldErrors.map((e) => e.targetField);
      issues.push({
        schema,
        message: `${unknownFieldNames.length} unknown field${unknownFieldNames.length !== 1 ? "s" : ""}: ${unknownFieldNames.join(", ")}`,
      });
    }
  }

  // Check for duplicates
  const duplicateErrors = validateDuplicateMappings(currentMappings);

  if (duplicateErrors.length > 0) {
    const duplicateFieldNames = duplicateErrors.map((e) => e.targetField);
    issues.push({
      schema,
      message: `${duplicateFieldNames.length} duplicate mapping${duplicateFieldNames.length !== 1 ? "s" : ""}: ${duplicateFieldNames.join(", ")}`,
    });
  }

  // Check for mutually exclusive composite/parts conflicts
  const mutuallyExclusiveErrors = validateMutuallyExclusiveFields(currentMappings);

  if (mutuallyExclusiveErrors.length > 0) {
    const conflictingFields = [...new Set(mutuallyExclusiveErrors.map((e) => e.targetField))];
    issues.push({
      schema,
      message: `Composite and split fields mapped simultaneously: ${conflictingFields.join(", ")}`,
    });
  }

  return issues;
};

/**
 * Validate all schemas in a transformation
 * Used for checking if a document has mapping issues that need review
 *
 * @param transformation - The document transformation (gates "not loaded yet")
 * @param template - The transformation template
 * @param availableSchemas - Array of schemas to validate
 * @param schemaDefinitions - Schema definitions retrieved from the API
 * @param records - Loaded extracted records from the windowed endpoint (#6755)
 * @param recordsComplete - True when no more pages exist — see
 *   validateTemplateMapping for why this changes the no-record-found outcome
 * @returns Array of validation issues across all schemas
 */
export const validateAllTemplates = (
  transformation: DocumentTransformation | null,
  template: TransformationTemplate | undefined,
  availableSchemas: TargetSchema[],
  schemaDefinitions: SchemaDefinitionsResponse | null | undefined,
  records: ExtractedRecord[],
  recordsComplete: boolean
): ValidationIssue[] => {
  if (!transformation || !template) return [];
  // No page has landed yet. Reporting "couldn't validate" here would flash the
  // error banner on every document while its first page loads; pre-#6755 the
  // validator was equally silent at this point (extracted_records not yet in
  // hand), so returning nothing preserves the original timing semantics.
  if (records.length === 0) return [];

  return availableSchemas.flatMap((schema) =>
    validateTemplateMapping(records, template, schema, schemaDefinitions, recordsComplete)
  );
};
